import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, IconButton, useTheme } from '@get-mosh/msh-shared';
import {
    MouseEventHandler,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { QuizContext } from '../Quiz.context';
import { QUIZ_EVENT_TYPES, QUIZ_QUESTION_TYPES } from 'src/types/Quiz';
import { trackClick } from 'src/analytics/Events';
import EventNames from 'src/analytics/EventNames';
import { Vertical } from 'src/constants/Verticals';
import { usePathname } from 'next/navigation';
import {
    faArrowLeftLong,
    faArrowRightLong,
    faArrowTurnDownLeft,
    faSpinnerThird,
} from '@fortawesome/pro-solid-svg-icons';

type QuizFooterProps = {
    variant?: 'default' | 'dark';
};

const QuizFooter = ({ variant }: QuizFooterProps) => {
    const pathname = usePathname();
    const {
        quizType,
        quizName,
        quizVersion,
        currentQuestion,
        currentQuestionIndex,
        lastCompletedQuestionIndex,
        handleNavigatePrevQuestion,
        handleNavigateNextQuestion,
        state,
        handleSubmitAnswer,
    } = useContext(QuizContext);
    const {
        questionText,
        additionalText: ctaButtonText,
        questionType,
        estimateTimeText: animationLength,
    } = currentQuestion || {};
    const [backDisabled, setBackDisabled] = state.backDisabled;
    const [backVisible, setBackVisible] = state.backVisible;
    const [forwardDisabled, setForwardDisabled] = state.forwardDisabled;
    const [forwardVisible, setForwardVisible] = state.forwardVisible;
    const [continueText] = state.continueText;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [continueVisible] = state.continueVisible;
    const [acceptVisible] = state.acceptVisible;
    const [loading] = state.loading;
    const { isMosh } = useTheme();
    const isDark = variant === 'dark' && isMosh;
    // Determine if back visible
    useEffect(() => {
        if (currentQuestionIndex > 0) {
            setBackVisible(true);
            setBackDisabled(false);
        } else {
            setBackVisible(false);
            setBackDisabled(true);
        }
    }, [
        currentQuestionIndex,
        lastCompletedQuestionIndex,
        setBackDisabled,
        setBackVisible,
        setForwardDisabled,
        setForwardVisible,
    ]);

    const handleContinueClick = useCallback(() => {
        window.dispatchEvent(new Event(QUIZ_EVENT_TYPES.continueClick));
    }, []);
    const handleForwardClick = useCallback(() => {
        trackClick(EventNames.QUIZ_BUTTON_CLICKED, {
            quiz_name: `${quizName} - ${quizVersion}`,
            vertical: Vertical[quizType.toUpperCase()],
            label: 'Forward',
            question_name: questionText,
            url: pathname,
        });
        window.dispatchEvent(new Event(QUIZ_EVENT_TYPES.forwardClick));
        handleNavigateNextQuestion();
    }, [
        handleNavigateNextQuestion,
        quizType,
        quizName,
        quizVersion,
        questionText,
        pathname,
    ]);

    const handleBackClick = useCallback(() => {
        trackClick(EventNames.QUIZ_BUTTON_CLICKED, {
            quiz_name: `${quizName} - ${quizVersion}`,
            vertical: Vertical[quizType.toUpperCase()],
            label: 'Back',
            question_name: questionText,
            url: pathname,
        });
        window.dispatchEvent(new Event(QUIZ_EVENT_TYPES.backClick));
        handleNavigatePrevQuestion();
    }, [
        handleNavigatePrevQuestion,
        quizType,
        quizName,
        quizVersion,
        questionText,
        pathname,
    ]);

    const onMouseDown: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.preventDefault();
    };

    const handleSubmit = useCallback(() => {
        if (currentQuestion.questionId) {
            setContinueDisabled(true);
            handleSubmitAnswer({
                questionId: currentQuestion.questionId,
                questionText: currentQuestion.questionText,
            });
        }
    }, [setContinueDisabled, currentQuestion, handleSubmitAnswer]);

    const quizLoadingQuestions = [
        QUIZ_QUESTION_TYPES.loading,
        QUIZ_QUESTION_TYPES.loadingGraph,
        QUIZ_QUESTION_TYPES.loadingImage,
    ];

    const isLoadingQuestion = quizLoadingQuestions.includes(questionType);

    const isButtonDisabled = isLoadingQuestion ? false : continueDisabled;

    const [width, setWidth] = useState(0); // Start at 0px

    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (isLoadingQuestion) {
            setWidth(0); // Reset width to 0 when loading starts
            interval = setInterval(() => {
                setWidth((prevWidth) => {
                    if (prevWidth < 115) {
                        // Stop at 1000px (or any desired max width)
                        return prevWidth + 2;
                    } else {
                        clearInterval(interval); // Stop the interval when max width is reached
                        return prevWidth;
                    }
                });
            }, 50); // Update every 1 millisecond
        } else {
            setWidth(0); // Reset width when loading stops
        }

        return () => clearInterval(interval); // Cleanup on component unmount
    }, [isLoadingQuestion]);

    return (
        <div
            className={classNames(
                'bottom-0 left-0 right-0 flex items-center justify-center border-b border-t py-4 shadow', // shared classes
                isDark ? 'border-green-faint-dark' : 'border-grey-200', // conditional border classes
            )}
        >
            <div
                className={classNames(
                    'max-w-[42.125rem]',
                    'container flex min-h-[50px]',
                    questionType === QUIZ_QUESTION_TYPES.onboarding
                        ? 'w-full justify-start'
                        : 'justify-between',
                )}
            >
                <div className={classNames('flex items-center')}>
                    <div className="flex gap-3">
                        {backVisible && (
                            <IconButton
                                variant={
                                    isDark ? 'secondaryLight' : 'secondary'
                                }
                                disabled={backDisabled}
                                onClick={handleBackClick}
                                onMouseDown={onMouseDown}
                                icon={faArrowLeftLong}
                            />
                        )}
                    </div>
                    <div className="flex gap-4">
                        {forwardVisible && (
                            <IconButton
                                variant={
                                    isDark ? 'secondaryLight' : 'secondary'
                                }
                                disabled={forwardDisabled}
                                onClick={handleForwardClick}
                                onMouseDown={onMouseDown}
                                icon={faArrowRightLong}
                            />
                        )}
                    </div>
                </div>
                {loading ? (
                    <div
                        className={classNames('flex items-center justify-end', {
                            'text-white': isDark,
                        })}
                    >
                        <FontAwesomeIcon
                            icon={faSpinnerThird}
                            className={'fa-spin'}
                            size={'2xl'}
                        />
                    </div>
                ) : (
                    <div
                        className={classNames('flex items-center gap-4', {
                            'w-full':
                                questionType === QUIZ_QUESTION_TYPES.onboarding,
                        })}
                    >
                        {questionType === QUIZ_QUESTION_TYPES.onboarding ? (
                            <div
                                className={classNames('space-x-2', {
                                    'w-full':
                                        questionType ===
                                        QUIZ_QUESTION_TYPES.onboarding,
                                })}
                            >
                                <Button
                                    data-testid="quiz-continue-button"
                                    text={
                                        ctaButtonText ? ctaButtonText : 'Start'
                                    }
                                    className="font-demi px-6 py-4 normal-case tracking-normal"
                                    onClick={handleSubmit}
                                    variant="primary"
                                />
                                <span
                                    className={classNames('hidden md:inline', {
                                        'text-white': isMosh,
                                        'text-grey-400': !isMosh,
                                    })}
                                >
                                    Press <strong>Enter</strong>{' '}
                                    <FontAwesomeIcon
                                        icon={faArrowTurnDownLeft}
                                    />
                                </span>
                            </div>
                        ) : continueVisible ? (
                            <>
                                <span
                                    className={classNames(
                                        isDark ? 'text-white' : 'text-grey-400',
                                        continueDisabled ? 'text-grey-300' : '',
                                        'ml-1',
                                        'hidden',
                                        'md:inline',
                                    )}
                                >
                                    Press <strong>Enter</strong>{' '}
                                    <FontAwesomeIcon
                                        icon={faArrowTurnDownLeft}
                                    />
                                </span>
                                <div className="relative">
                                    {isLoadingQuestion &&
                                    animationLength != '0' ? (
                                        <div
                                            className={classNames(
                                                'pointer-events-none absolute inset-0 z-20 rounded-lg bg-[#22222240] transition-all duration-300 ease-in-out',
                                                'w-0',
                                            )}
                                            style={{ width: `${width}px` }}
                                        ></div>
                                    ) : null}
                                    <Button
                                        data-testid="quiz-continue-button"
                                        text={continueText ?? 'Continue'}
                                        disabled={isButtonDisabled}
                                        className="normal-case tracking-normal"
                                        onClick={
                                            isLoadingQuestion
                                                ? handleSubmit
                                                : handleContinueClick
                                        }
                                        variant="primary"
                                    />
                                </div>
                            </>
                        ) : acceptVisible ? (
                            <>
                                <Button
                                    text="I have more questions"
                                    className="hover:text-green-dark"
                                    variant="tertiary"
                                    onClick={() =>
                                        window.dispatchEvent(
                                            new Event(
                                                QUIZ_EVENT_TYPES.acceptClick,
                                            ),
                                        )
                                    }
                                />
                                <Button
                                    text="Accept Plan"
                                    className="normal-case tracking-normal"
                                />
                            </>
                        ) : (
                            <div className="px-6 py-4"></div> // Empty div with the same padding and margin as the Button
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuizFooter;
